/**
 * Available Status Utilities
 *
 * Deleted is hidden from the UI for all users; every other status (including
 * Triaged/Detected) is available to everyone.
 */

import { RiskStatus } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

/**
 * Get list of status values available in UI (all except Deleted).
 */
export function getAvailableStatuses(): RiskStatus[] {
  return Object.values(RiskStatus).filter(
    status => status !== RiskStatus.Deleted
  );
}

/**
 * Get dropdown options for available statuses.
 */
export function getAvailableStatusOptions(): DropdownOption[] {
  return getAvailableStatuses().map(status => ({
    value: status,
    label: getStatusLabel(status),
    id: status,
  }));
}

/**
 * Get export options for available statuses.
 */
export function getAvailableStatusExportOptions(): Array<{
  label: string;
  value: RiskStatus;
}> {
  return getAvailableStatuses().map(status => ({
    label: getStatusLabel(status),
    value: status,
  }));
}

/**
 * Type guard to check if a value is a valid RiskStatus enum value.
 */
export function isValidRiskStatus(value: unknown): value is RiskStatus {
  return (
    typeof value === 'string' &&
    Object.values(RiskStatus).includes(value as RiskStatus)
  );
}

/**
 * Check if a status is available in the UI. Deleted is hidden for all users;
 * everything else is available.
 */
export function isStatusAvailable(status: RiskStatus): boolean {
  return status !== RiskStatus.Deleted;
}
